/**
 * MentionInput — textarea wrapper with @-mention autocomplete.
 *
 * Stores mention tokens in content as `@[Name](userId)` and exposes a parallel
 * `mentions: [{user_id, name}]` array via onChange.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { User } from 'lucide-react';
import api from '../utils/api';
import { mediaUrl } from '../utils/media';

const ROLE_LABEL = {
  driver: 'Driver', carrier_owner: 'Carrier', dispatcher: 'Dispatcher',
  learner: 'Learner', affiliate: 'Affiliate', service_provider: 'Provider', admin: 'Admin',
};

export const MentionInput = ({
  value,
  mentions = [],
  onChange,            // (newValue, newMentions) => void
  placeholder = '',
  rows = 3,
  className = '',
  testId,
  autoFocus = false,
}) => {
  const taRef = useRef(null);
  const [query, setQuery] = useState(null);   // {start, q}
  const [results, setResults] = useState([]);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (autoFocus) taRef.current?.focus();
  }, [autoFocus]);

  const fetchUsers = useCallback(async (q) => {
    if (!q) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await api.get('/community/users/search', { params: { q } });
      setResults(res.data.users || []);
      setHighlight(0);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!query) { setResults([]); return; }
    const t = setTimeout(() => fetchUsers(query.q), 180);
    return () => clearTimeout(t);
  }, [query, fetchUsers]);

  const handleChange = (e) => {
    const newVal = e.target.value;
    const caret = e.target.selectionStart || 0;
    // Look backwards from caret to find an active @ token
    const upto = newVal.slice(0, caret);
    const m = upto.match(/(?:^|\s)@([\w][\w\s-]{0,30})$/);
    if (m) {
      setQuery({ start: caret - m[1].length - 1, q: m[1].trim() });
    } else {
      setQuery(null);
    }
    // Recompute mentions: only keep mentions whose @[Name](userId) token is still present
    const stillThere = mentions.filter(mt => newVal.includes(`@[${mt.name}](${mt.user_id})`));
    onChange(newVal, stillThere);
  };

  const insertMention = (u) => {
    if (!query) return;
    const ta = taRef.current;
    const before = value.slice(0, query.start);
    const after = value.slice((ta?.selectionStart ?? 0));
    const token = `@[${u.name}](${u.id}) `;
    const newVal = before + token + after;
    const newMentions = [...mentions, { user_id: u.id, name: u.name }];
    setQuery(null);
    setResults([]);
    onChange(newVal, newMentions);
    setTimeout(() => {
      if (ta) {
        const pos = (before + token).length;
        ta.focus();
        ta.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const onKeyDown = (e) => {
    if (!query || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + results.length) % results.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(results[highlight]); }
    else if (e.key === 'Escape') { setQuery(null); setResults([]); }
  };

  return (
    <div className="relative">
      <textarea
        ref={taRef}
        value={value}
        rows={rows}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        className={className}
        data-testid={testId}
      />
      {query && (results.length > 0 || loading) && (
        <div className="absolute z-30 left-0 right-0 mt-1 max-w-md bg-white border border-slate-200 rounded-lg shadow-xl overflow-hidden" data-testid="mention-dropdown">
          {loading && results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-400">Searching…</div>
          ) : (
            results.map((u, i) => (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertMention(u); }}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${i === highlight ? 'bg-sky-50' : 'hover:bg-slate-50'}`}
                data-testid={`mention-option-${u.id}`}
              >
                <span className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {u.profile_picture
                    ? <img src={mediaUrl(u.profile_picture)} alt="" className="w-full h-full object-cover" />
                    : <User className="h-3.5 w-3.5 text-slate-500" />}
                </span>
                <span className="font-medium text-slate-800 flex-1 truncate">{u.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{ROLE_LABEL[u.role] || u.role}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default MentionInput;
